import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
  HostBinding,
  Optional,
  Self,
  ElementRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NgControl,
  Validators
} from '@angular/forms';
import {FocusMonitor} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {MatFormField, MatFormFieldControl} from '@angular/material/form-field';
import {startWith, map, shareReplay, distinctUntilChanged, take, tap, withLatestFrom} from 'rxjs/operators';
import {Observable, Subject, of, merge} from 'rxjs';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';

import {CountryModel} from 'src/app/shared/models/CountryModel';
import {CountriesService} from 'src/app/services/countries.service';

@Component({
  selector: 'terra-country-input',
  templateUrl: './country-input.component.html',
  styleUrls: ['./country-input.component.scss'],
  providers: [{provide: MatFormFieldControl, useExisting: CountryInputComponent}],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CountryInputComponent extends OnDestroyMixin implements MatFormFieldControl<number>, ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() inputId = '';

  @Input() get value(): number | null {
    // console.info('CountryID was read. Value = ' + this._value);
    return this._value;
  }

  @Input() get placeholder() {
    return this._placeholder || '';
  }

  @Input() get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  get required() {
    return this._required;
  }

  @Input('aria-describedby') userAriaDescribedBy: string;

  @Input() countriesList: CountryModel[];

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  alert = alert;

  static nextId = 0;
  private nextId = CountryInputComponent.nextId++;
  private _required = false;
  private _placeholder: string;
  private _value: number;
  private _disabled = false;
  private countriesListSubject$ = new Subject<CountryModel[]>();
  private allCountries$ = merge(of(this.countriesService.allCountries), this.countriesListSubject$).pipe(shareReplay(1));

  controlType = 'terra-country-input';
  autofilled?: boolean;
  isAutoFilled = false;
  focused = false;
  formControl = new UntypedFormControl();
  stateChanges = new Subject<void>();

  @HostBinding() id = `terra-country-input-${this.nextId}`;

  onChange = (_: any) => {
  };
  onTouched = () => {
  };

  /** This is used when setting the controlValue from outside */
  set value(countryId: number | null) {
    if (countryId) {
      this.allCountries$.pipe(take(1)).subscribe(allCountries => {
        const country = allCountries.find(c => c.id === countryId);
        this.formControl.setValue(country?.name);
        this._value = country?.id;
        this.stateChanges.next();
      });
    } else {
      this._value = null;
      this.stateChanges.next();
    }
  }

  private setValueInternal(countryId: number) {
    if (countryId) {
      this.allCountries$.pipe(take(1)).subscribe(allCountries => {
        const country = allCountries.find(c => c.id === countryId);
        this._value = country?.id;
        this.stateChanges.next();
        if (this.onChange) {
          this.onChange(countryId);
        }
      });
    } else {
      this._value = null;
      this.stateChanges.next();
      if (this.onChange) {
        this.onChange(null);
      }
    }
  }

  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  get empty() {
    return !this._value;
  }

  set required(req: boolean) {
    const isRequired = coerceBooleanProperty(req);
    this._required = isRequired;

    this.formControl.setValidators(isRequired ? Validators.required : []);
    this.formControl.updateValueAndValidity();

    this.stateChanges.next();
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.formControl.disable() : this.formControl.enable();
    this.stateChanges.next();
  }

  get errorState(): boolean {
    this.ngControl.control.updateValueAndValidity();
    return this.ngControl.errors !== null && !!this.ngControl.touched;
  }

  filteredCountries$: Observable<CountryModel[]> = this.formControl.valueChanges.pipe(
    startWith(''),
    distinctUntilChanged(),
    withLatestFrom(this.allCountries$),
    map(([formControlValue, countriesList]) => {
      if (!formControlValue) {
        return countriesList;
      }
      return this.filterCountriesByName(formControlValue, countriesList);
    }),
    shareReplay(1)
  );

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() public parentFormField: MatFormField,
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private countriesService: CountriesService
  ) {
    super();

    _focusMonitor.monitor(_elementRef, true)
      .subscribe(origin => {
        if (this.focused && !origin) {
          this.onTouched();
        }
        this.focused = !!origin;
        this.stateChanges.next();
      });

    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update the countries list when a new list is providedSA
    if (changes.countriesList) {
      this.countriesListSubject$.next(changes.countriesList.currentValue);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  ngOnInit() {
    this.handleCountryNameFullMatches();
  }

  writeValue(countryId: number | string): void {
    this.value = countryId ? +countryId : null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // part of mat-input
  setDescribedByIds(ids: string[]): void {
    const controlElement = this._elementRef.nativeElement
      .querySelector('.country-input');
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  // part of mat-input
  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this._elementRef.nativeElement.querySelector('input').focus();
    }
  }

  conutryTrackByFn(index, item: CountryModel) {
    return item.id; // or index
  }

  selectFromAutocomplete(countryName: string) {
    this.filteredCountries$.pipe(take(1))
      .subscribe(countries => {
        this.formControl.setValue(countries.find(c => c.name.toLowerCase() === countryName.toLowerCase())?.name);
      });
  }

  setAutofillState(state: boolean) {
    this.isAutoFilled = state;
  }

  private filterCountriesByName(filter: string, list: CountryModel[]): CountryModel[] {
    filter = (filter || '').trim().toLocaleLowerCase();
    if (filter == '') {
      return list;
    }
    return list.filter(country => country.name.toLowerCase().startsWith(filter));
  }

  private handleCountryNameFullMatches() {
    this.formControl.valueChanges.pipe(
      map(filter => filter?.trim()?.toLowerCase() || ''),
      distinctUntilChanged(),
      withLatestFrom(this.filteredCountries$),
      untilComponentDestroyed(this),
      tap(([filter, countries]) => {
        // We don't want to allow browser autofill because it may fill a country which is not in the list.
        // If the filter was autofilled (or if the user cleard the input), remove the value.
        if (this.isAutoFilled || !filter) {
          this.setValueInternal(null);
        } else {
          const countryToSelect = countries.find(c => c.name.trim().toLowerCase() === filter);
          if (countryToSelect) {
            this.setValueInternal(countryToSelect.id);
          }
        }
      })
    ).subscribe();
  }
}
